import React, { useState, useEffect, useCallback } from 'react';
import { impressionsFor } from '../utils/reportMetrics';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Search,
  RefreshCw,
  Users,
  PlusCircle,
  FileText,
  Key,
  Download,
  Pencil,
  Settings,
  Trash2,
  Loader2,
  Gamepad2,
  X,
  Menu,
  Gauge,
  TrendingUp,
} from 'lucide-react';
import ContentScannerModal from '../components/ContentScannerModal';
import CampaignAutomationSettings from '../components/CampaignAutomationSettings';
import LiveStreamMonitor from '../components/LiveStreamMonitor';
import CampaignContentView from '../components/CampaignContentView';
import CampaignCreatorsList from '../components/CampaignCreatorsList';
import BulkCreatorUpload from '../components/BulkCreatorUpload';
import AddContentModal from '../components/AddContentModal';
import RejectedContentBox from '../components/RejectedContentBox';
import { useAuth } from '../context/AuthContext';
import EditCampaignModal from '../components/EditCampaignModal';
import ExportOptionsModal from '../components/ExportOptionsModal';
import KeyUploadModal from '../components/KeyUploadModal';
import YouTubeEstimateTooltip from '../components/YouTubeEstimateTooltip';
import CampaignKpiProgress from '../components/CampaignKpiProgress';
import CampaignSteamTab from '../components/CampaignSteamTab';
import { campaignsAPI } from '../api/campaigns';
import { contentAPI } from '../api/content';
import { formatPlatformName } from '../utils/platformHelpers';

const CampaignDetail = () => {
  // Two views of the same campaign: what our creators produced (Performance),
  // and what the game itself did while they produced it (Campaign Analysis).
  // Tabbed rather than stacked because the page is already long, and the two
  // answer different questions — nobody reads both at once.
  const [activeTab, setActiveTab] = useState('performance');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  // Instagram API demo pull is only surfaced in the App Review test org.
  const isReviewer = (user?.organization?.id || user?.organizationId) === '00f4e0f7-d1fa-46f9-8a7d-f1f95a48f4b7';
  const [pullingInstagram, setPullingInstagram] = useState(false);
  const [instagramPullMsg, setInstagramPullMsg] = useState(null);

  const handleInstagramPull = async () => {
    setPullingInstagram(true);
    setInstagramPullMsg(null);
    try {
      const res = await contentAPI.instagramPullCampaign(id);
      const d = res.data || {};
      if (!d.matchedCreators) {
        setInstagramPullMsg(`No creator on this campaign has the Instagram handle @${d.username}. Add that creator (with their Instagram) via "Add Creators" first.`);
      } else {
        const added = (d.ingested || 0) + (d.relinked || 0);
        setInstagramPullMsg(`Pulled from Instagram (@${d.username}): ${added} post${added === 1 ? '' : 's'} added/updated, ${d.skipped || 0} already up to date.`);
      }
      fetchCampaign(true, false);
    } catch (e) {
      setInstagramPullMsg(e.response?.data?.error || 'Instagram pull failed.');
    } finally {
      setPullingInstagram(false);
    }
  };
  const { id } = useParams();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState(null);
  const [contents, setContents] = useState([]);
  const [creators, setCreators] = useState([]);
  const [loading, setLoading] = useState(true);

  // Modal states
  const [showScanner, setShowScanner] = useState(false);
  const [showAutomation, setShowAutomation] = useState(false);
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [showAddContent, setShowAddContent] = useState(false);
  const [showEditCampaign, setShowEditCampaign] = useState(false);
  const [showExportOptions, setShowExportOptions] = useState(false);
  const [showKeyUpload, setShowKeyUpload] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showLiveWidget, setShowLiveWidget] = useState(false);
  const [showActionsMenu, setShowActionsMenu] = useState(false);
  const [showCreatorsWidget, setShowCreatorsWidget] = useState(false);

  // Stream stats states
  const [refreshingStreamStats, setRefreshingStreamStats] = useState(false);
  const [streamStatsResult, setStreamStatsResult] = useState(null);
  const [refreshError, setRefreshError] = useState(null);
  const [refreshSuccess, setRefreshSuccess] = useState(false);
  const [refreshProgress, setRefreshProgress] = useState(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  const processCampaignData = (campaign) => {
    const creatorsList = campaign?.influencers?.map(inf => ({
      ...inf.creator,
      isSponsored: inf.isSponsored || false,
      agreedRate: inf.agreedRate || null,
      currency: inf.currency || 'GBP',
      pauseWindows: inf.pauseWindows || null
    })) || [];
    setCreators(creatorsList);

    const allContents = [];
    const seenKeys = new Set();
    if (campaign?.influencers) {
      campaign.influencers.forEach(inf => {
        if (inf.content) {
          inf.content.forEach(content => {
            // Same dedup key as the backend (campaign.js / reports.js): a bare channel
            // URL (e.g. twitch.tv/afrodited13) is shared by every one of a creator's
            // streams, so never collapse on it — fall back to the unique content id.
            const nUrl = content.contentUrl
              ? content.contentUrl.toLowerCase().replace('://www.', '://').replace(/\/+$/, '')
              : null;
            let urlVideoId = null;
            if (nUrl) {
              const tw = nUrl.match(/\/videos\/(\d+)/);
              const tk = nUrl.match(/\/video\/(\d+)/);
              const yt = nUrl.match(/(?:[?&]v=|\/shorts\/|youtu\.be\/)([a-z0-9_-]+)/i);
              urlVideoId = (tw && `tw_${tw[1]}`) || (tk && `tk_${tk[1]}`) || (yt && `yt_${yt[1]}`) || null;
            }
            const isSpecificVodUrl = nUrl && /\/(videos|video|watch|v|shorts)\/[\w-]+/.test(nUrl);
            const key = content.vodId || urlVideoId || content.videoId || (isSpecificVodUrl ? nUrl : null) || content.id;
            if (!seenKeys.has(key)) {
              seenKeys.add(key);
              allContents.push({ ...content, isSponsored: inf.isSponsored || false });
            }
          });
        }
      });
    }
    setContents(allContents);
  };

  const fetchCampaign = useCallback(async (skipBackfill = false, skipSegments = false) => {
    try {
      const url = new URL(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${id}`);
      if (skipBackfill) url.searchParams.append('skipBackfill', 'true');
      if (skipSegments) url.searchParams.append('skipSegments', 'true');

      const response = await fetch(url.toString(), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });

      const data = await response.json();
      const campaign = data.campaign;
      setCampaign(campaign);
      processCampaignData(campaign);
      return campaign;
    } catch (error) {
      console.error('Error fetching campaign:', error);
    }
  }, [id]);

  const fillMissingSegments = useCallback(async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 30000);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${id}/fill-missing-segments`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` },
          signal: controller.signal
        }
      );
      clearTimeout(timeoutId);

      if (!response.ok) throw new Error('Failed to fill missing segments');
      const data = await response.json();
      console.log(`Filled ${data.segmentsCreated} segments for ${data.processed} content items`);
      if (data.segmentsCreated > 0) await fetchCampaign();
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error filling segments:', error);
    }
  }, [id, fetchCampaign]);

  const handleQuickRefresh = async () => {
    setRefreshingStreamStats(true);
    setRefreshError(null);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${id}?skipBackfill=true&skipSegments=true`,
        { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
      );
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      setCampaign(data.campaign);
      processCampaignData(data.campaign);
      setRefreshError(null);
    } catch (error) {
      console.error('Error refreshing campaign:', error);
      setRefreshError(error.message || 'Failed to refresh.');
    } finally {
      setRefreshingStreamStats(false);
    }
  };

  const handleRefreshGameStats = async () => {
    setRefreshingStreamStats(true);
    setStreamStatsResult(null);
    setRefreshProgress(null);

    const pollInterval = setInterval(async () => {
      try {
        const progressRes = await fetch(
          `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${id}/refresh-progress`,
          { headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` } }
        );
        const progressData = await progressRes.json();
        setRefreshProgress(progressData);

        if (progressData.status === 'complete' || progressData.status === 'error') {
          clearInterval(pollInterval);
          setRefreshingStreamStats(false);

          if (progressData.status === 'complete') {
            setStreamStatsResult({
              total: progressData.total, updated: progressData.updated,
              failed: progressData.failed, skipped: progressData.skipped,
              byPlatform: progressData.byPlatform
            });
            if (progressData.updated > 0) fetchCampaign(true, true);
          } else {
            setStreamStatsResult({ error: progressData.message });
          }
          setTimeout(() => setRefreshProgress(null), 3000);
        }
      } catch (err) {
        console.error('Error polling refresh progress:', err);
      }
    }, 1000);

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${id}/refresh-game-stats`,
        {
          method: 'POST',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to start refresh');
      }
    } catch (error) {
      clearInterval(pollInterval);
      setStreamStatsResult({ error: error.message });
      setRefreshingStreamStats(false);
      setRefreshProgress(null);
    }
  };

  const handleExportCSV = async (options = {}) => {
    setExporting(true);
    try {
      const exportUrl = new URL(`${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${id}/export`);
      if (options.startDate) exportUrl.searchParams.append('startDate', options.startDate);
      if (options.endDate) exportUrl.searchParams.append('endDate', options.endDate);
      if (options.platform && options.platform !== 'all') exportUrl.searchParams.append('platform', options.platform);
      if (options.contentType && options.contentType !== 'all') exportUrl.searchParams.append('contentType', options.contentType);

      const response = await fetch(exportUrl.toString(), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
      });
      if (!response.ok) throw new Error('Failed to export campaign data');

      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `${campaign?.name || 'campaign'}-export.csv`;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="(.+)"/);
        if (match) filename = match[1];
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error exporting campaign:', error);
      alert('Failed to export campaign data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleDeleteCampaign = async () => {
    setDeleting(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:5000'}/api/campaigns/${id}`,
        {
          method: 'DELETE',
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      if (!response.ok) throw new Error('Failed to delete campaign');
      navigate('/campaigns');
    } catch (error) {
      console.error('Error deleting campaign:', error);
      alert('Failed to delete campaign. Please try again.');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  useEffect(() => {
    const loadCampaignFast = async () => {
      await fetchCampaign(true, true);
      setLoading(false);

      setTimeout(() => {
        fetchCampaign(false, false).catch(err => console.warn('Background fetch failed:', err));
      }, 500);

      setTimeout(() => {
        if (campaign && campaign.influencers) {
          const hasMissingSegments = campaign.influencers.some(inf =>
            inf.content?.some(content => !content.segments || content.segments.length === 0)
          );
          if (hasMissingSegments) {
            console.log('[Auto-fill] Found content with missing segments, filling...');
            fillMissingSegments();
          }
        }
      }, 1000);
    };
    loadCampaignFast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, fetchCampaign, fillMissingSegments]);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <Loader2 size={32} className="animate-spin text-sc-pink" />
        <p className="mt-4 text-white text-sm font-medium">Loading campaign...</p>
      </div>
    );
  }

  const gameImage = campaign?.game?.coverImageUrl;

  // Action button helper
  const ActionBtn = ({ onClick, disabled, icon: Icon, label, variant = 'default' }) => {
    const variants = {
      primary: 'bg-sc-pink/15 border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25',
      cyan: 'bg-cyan-500/15 border-cyan-500/25 text-cyan-400 hover:bg-cyan-500/25',
      default: 'bg-white/5 border-white text-white hover:text-white hover:bg-white/10',
      danger: 'bg-red-500/10 border-red-500/20 text-red-400/70 hover:text-red-400 hover:bg-red-500/20',
    };
    return (
      <button
        onClick={onClick}
        disabled={disabled}
        className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all disabled:opacity-50 ${variants[variant]}`}
      >
        <Icon className="w-3.5 h-3.5" />
        {label}
      </button>
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Campaign Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          {gameImage ? (
            <img src={gameImage} alt="" className="w-14 h-14 rounded-xl object-cover ring-1 ring-white/10" />
          ) : (
            <div className="w-14 h-14 rounded-xl bg-white/5 border border-white flex items-center justify-center">
              <Gamepad2 className="w-6 h-6 text-white/20" />
            </div>
          )}
          <div>
            <h1 className="text-2xl font-bold text-white">{campaign?.name}</h1>
            <div className="flex items-center gap-3 mt-1">
              {campaign?.game && (
                <span className="text-sm text-sc-pink/70">{campaign.game.title}</span>
              )}
              <span className="text-sm text-white">
                {creators.length} creators · {contents.length} content pieces
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-1.5">
          <ActionBtn onClick={() => setShowScanner(true)} icon={Search} label="Scan" variant="primary" />
          {isReviewer && (
            <ActionBtn
              onClick={handleInstagramPull}
              disabled={pullingInstagram}
              icon={RefreshCw}
              label={pullingInstagram ? 'Pulling…' : 'Pull Instagram (API)'}
              variant="cyan"
            />
          )}
          {isReviewer && instagramPullMsg && (
            <span className="text-xs text-white max-w-[240px] truncate" title={instagramPullMsg}>{instagramPullMsg}</span>
          )}
          <ActionBtn
            onClick={handleRefreshGameStats}
            disabled={refreshingStreamStats}
            icon={RefreshCw}
            label={refreshingStreamStats ? 'Refreshing...' : 'Refresh'}
            variant="cyan"
          />

          <div className="relative">
            <button
              onClick={() => setShowActionsMenu(prev => !prev)}
              className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border bg-sc-pink/15 border-sc-pink/25 text-sc-pink hover:bg-sc-pink/25 text-xs font-medium transition-all"
            >
              <Menu className="w-3.5 h-3.5" />
            </button>

            {showActionsMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowActionsMenu(false)} />
                <div className="absolute right-0 top-full mt-1 z-50 w-48 bg-[#1a1525] border border-white rounded-xl shadow-2xl shadow-black/50 py-1 overflow-hidden">
                  {[
                    { icon: Users, label: 'Add Creators', onClick: () => setShowBulkUpload(true) },
                    { icon: PlusCircle, label: 'Add Content', onClick: () => setShowAddContent(true) },
                    { icon: FileText, label: 'Client Report', onClick: () => navigate(`/client-report/${id}/new`) },
                    { icon: Key, label: 'Add Keys', onClick: () => setShowKeyUpload(true) },
                    { icon: Download, label: exporting ? 'Exporting...' : 'Export Report', onClick: () => setShowExportOptions(true), disabled: exporting },
                    { icon: Pencil, label: 'Edit Campaign', onClick: () => setShowEditCampaign(true) },
                    { icon: Settings, label: 'Automation', onClick: () => setShowAutomation(true) },
                    ...(isAdmin ? [
                      { divider: true },
                      { icon: Trash2, label: 'Delete', onClick: () => setShowDeleteConfirm(true), danger: true },
                    ] : []),
                  ].map((item, i) => item.divider ? (
                    <div key={i} className="border-t border-white/10 my-1" />
                  ) : (
                    <button
                      key={i}
                      onClick={() => { setShowActionsMenu(false); item.onClick(); }}
                      disabled={item.disabled}
                      className={`w-full flex items-center gap-2.5 px-3 py-2 text-xs font-medium transition-colors disabled:opacity-50 ${
                        item.danger
                          ? 'text-red-400 hover:bg-red-500/10'
                          : 'text-white hover:bg-white/8'
                      }`}
                    >
                      <item.icon className="w-3.5 h-3.5 flex-shrink-0" />
                      {item.label}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Refresh Error */}
      {refreshError && (
        <div className="mb-6 px-4 py-3 rounded-xl text-sm font-medium border bg-red-500/10 border-red-500/20 text-red-400 flex items-center justify-between">
          <span>Failed to refresh: {refreshError}</span>
          <button onClick={() => setRefreshError(null)} className="text-red-400/60 hover:text-red-400">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Refresh Progress */}
      {refreshingStreamStats && refreshProgress && (refreshProgress.status === 'running' || refreshProgress.status === 'starting') && (
        <div className="mb-6 px-4 py-3 rounded-xl text-sm font-medium border bg-sc-pink/10 border-sc-pink/25">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sc-pink">
              {refreshProgress.message || 'Refreshing content...'}
            </span>
            <span className="text-white">
              {refreshProgress.processed || 0}/{refreshProgress.total || 0}
            </span>
          </div>
          <div className="w-full bg-white/5 rounded-full h-2">
            <div
              className="bg-sc-pink h-2 rounded-full transition-all duration-300"
              style={{ width: `${refreshProgress.progress || 0}%` }}
            />
          </div>
          {refreshProgress.platforms && (
            <div className="mt-2 flex gap-4 text-xs text-white">
              {Object.entries(refreshProgress.platforms).map(([platform, count]) => (
                <span key={platform}>{formatPlatformName(platform)}: {count}</span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stream Stats Result */}
      {streamStatsResult && (
        <div className={`mb-6 px-4 py-3 rounded-xl text-sm font-medium border ${
          streamStatsResult.error
            ? 'bg-red-500/10 border-red-500/20 text-red-400'
            : 'bg-green-500/10 border-green-500/20 text-green-400'
        }`}>
          {streamStatsResult.error
            ? `Error: ${streamStatsResult.error}`
            : `Updated ${streamStatsResult.updated}/${streamStatsResult.total} streams`
          }
        </div>
      )}

      {/* View switcher */}
      <div className="flex items-center gap-1 border-b border-white/15 mb-5">
        {[
          { key: 'performance', label: 'Performance', Icon: Gauge },
          { key: 'steam', label: 'Campaign Analysis', Icon: TrendingUp },
        ].map(({ key, label, Icon }) => (
          <button
            key={key}
            onClick={() => setActiveTab(key)}
            className={`flex items-center gap-1.5 px-4 py-2 text-sm font-semibold rounded-t-lg transition-all ${
              activeTab === key
                ? 'text-white bg-[#241d33] border border-b-0 border-white'
                : 'text-white hover:text-sc-pink'
            }`}
          >
            <Icon className="w-3.5 h-3.5" /> {label}
          </button>
        ))}
      </div>

      {activeTab === 'steam' && <CampaignSteamTab campaignId={id} />}

      {activeTab === 'performance' && (<>
      {/* Campaign KPIs (internal, auto-measured from tracked content) */}
      <CampaignKpiProgress kpis={campaign?.kpis} contents={contents} keysAvailable={campaign?.keysAvailable} />

      {/* Rejected content review queue — high up so it can't be missed (auto-hides when empty) */}
      <RejectedContentBox campaignId={id} />

      {/* Sponsored & Organic Side-by-Side */}
      {(() => {
        const formatNum = (num) => num?.toLocaleString() || '0';
        const isStream = (c) => c.platform === 'twitch' || c.platform === 'kick' || ((c.platform === 'youtube') && (c.contentType === 'stream' || c.contentType === 'vod'));
        const isShortForm = (c) => c.platform === 'tiktok' || c.contentType === 'short';
        const isLongFormVideo = (c) => !isStream(c) && !isShortForm(c) && (c.platform === 'youtube' || c.platform === 'instagram');
        // Total reach per piece: a stream counts by its VOD views, everything else
        // by its views — so "Views" + "VOD Views" don't double-count and add up.
        const reachOf = (c) => isStream(c) ? (c.vodViews || 0) : (c.views || 0);

        const Pill = ({ label, value }) => (
          <span className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 bg-gradient-to-r from-pink-500/20 to-purple-500/20 border border-sc-pink">
            <span className="text-[11px] text-white">{label}</span>
            <span className="text-[11px] font-semibold text-white">{value}</span>
          </span>
        );

        const platformDisplay = (platform) => formatPlatformName(platform) || '-';

        // Split by the per-content Paid flag (matches the Campaign KPIs).
        const paidCreatorIds = new Set(contents.filter(c => c.isPaid).map(c => c.creatorId || c.creator?.id).filter(Boolean));
        // Sponsored = creators with a budget OR any Paid-marked content.
        const sponsoredCreators = creators.filter(c => (c.isSponsored && c.agreedRate > 0) || paidCreatorIds.has(c.id));
        // Organic = every piece NOT marked Paid, from ANY creator (so a sponsored
        // creator's organic content shows here too, not just non-sponsored creators).
        const organicContents = contents.filter(c => !c.isPaid);
        const oCreatorCount = new Set(organicContents.map(c => c.creatorId || c.creator?.id).filter(Boolean)).size;

        if (sponsoredCreators.length === 0 && organicContents.length === 0) return null;

        // --- Sponsored data ---
        const currencySymbols = { GBP: '£', USD: '$', EUR: '€' };
        const getCurrencySymbol = (code) => currencySymbols[code] || code;
        const formatMoney = (val, symbol) => {
          if (!val || val === 0) return `${symbol}0`;
          return val < 0.01 ? `${symbol}${val.toFixed(4)}` : `${symbol}${val.toFixed(2)}`;
        };

        const creatorROI = sponsoredCreators.map(creator => {
          const sym = getCurrencySymbol(creator.currency || 'GBP');
          const cost = creator.agreedRate || 0;
          const inf = campaign?.influencers?.find(i => i.creatorId === creator.id);
          const creatorContents = contents.filter(content =>
            inf?.content?.some(c => c.id === content.id)
          );
          // Sponsored Creator ROI reflects the PAID deliverables only — a sponsored
          // creator's organic-marked content must not inflate this table (content
          // count, stream duration, combined CCV, CPV/hr, views, etc.).
          const paidContents = creatorContents.filter(c => c.isPaid);

          const streams = paidContents.filter(isStream);
          const longform = paidContents.filter(isLongFormVideo);
          const shortform = paidContents.filter(isShortForm);
          const videoCount = longform.length + shortform.length;

          const streamSessionCount = (() => {
            if (streams.length === 0) return 0;
            const durations = streams.map(c => c.streamDuration || 0).filter(d => d > 0).sort((a, b) => a - b);
            if (durations.length === 0) return streams.length;
            let sessions = 1;
            for (let i = 1; i < durations.length; i++) {
              if (durations[i] - durations[i - 1] > 15) sessions++;
            }
            return sessions;
          })();

          // Use game segment duration when available for accurate per-game metrics
          const getSegmentDuration = (c) => {
            const segs = c.segments?.filter(s => s.gameId === campaign?.gameId || s.game?.id === campaign?.gameId);
            return (segs && segs.length > 0) ? segs.reduce((sum, s) => sum + (s.durationMinutes || 0), 0) : (c.streamDuration || c.duration || 0);
          };
          const getSegmentCCV = (c) => {
            const segs = c.segments?.filter(s => s.gameId === campaign?.gameId || s.game?.id === campaign?.gameId);
            if (segs && segs.length > 0) {
              const avg = segs.reduce((sum, s) => sum + (s.avgCcv || 0), 0) / segs.length;
              return avg || c.streamAvgCcv || c.avgCcv || 0;
            }
            return c.streamAvgCcv || c.avgCcv || 0;
          };
          const streamHours = streams.reduce((sum, c) => sum + getSegmentDuration(c), 0) / 60;
          const streamCombinedCCV = streams.reduce((sum, c) => sum + getSegmentCCV(c), 0);
          // Viewer-hours accumulate PER STREAM (ccv × hours). Summing ccv and hours
          // separately then multiplying (Σccv × Σhours) over-inflates the
          // denominator ~N× and collapses CPV/hr toward 0.
          const streamViewerHours = streams.reduce((sum, c) => sum + getSegmentCCV(c) * (getSegmentDuration(c) / 60), 0);
          const streamCPV = streamViewerHours > 0 ? cost / streamViewerHours : null;
          const streamVodViews = streams.reduce((sum, c) => sum + (c.vodViews || 0), 0);
          const longformViews = longform.reduce((sum, c) => sum + (c.views || 0), 0);
          const shortformViews = shortform.reduce((sum, c) => sum + (c.views || 0), 0);
          // Reach: streams by VOD views, videos/social by views (no double-count).
          const totalViews = paidContents.reduce((sum, c) => sum + reachOf(c), 0);
          // Summed PER ITEM: YouTube content posted on/after 2026-08-24 is
          // estimated at 5x rather than 10x.
          const totalImpressions = paidContents.reduce((sum, c) => sum + impressionsFor(c, reachOf(c)), 0);
          // Paid reach for CPV/CPM: each paid piece's real views across platforms
          // (video/social = views; Twitch/YT streams carry reach in vodViews).
          const paidReach = paidContents.reduce((sum, c) => sum + Math.max(c.views || 0, c.vodViews || 0), 0);
          const totalEngagement = paidContents.reduce((sum, c) => sum + (c.likes || 0) + (c.comments || 0) + (c.shares || 0), 0);

          return {
            ...creator, sym, cost, totalViews, totalImpressions, totalEngagement, paidReach,
            streams, longform, shortform,
            streamCount: streamSessionCount, videoCount, longformCount: longform.length, shortformCount: shortform.length,
            streamHours, streamCombinedCCV: Math.round(streamCombinedCCV), streamViewerHours, streamCPV,
            longformViews, shortformViews, streamVodViews,
            contentCount: paidContents.length
          };
        });

        const sTotalBudgetSpent = sponsoredCreators.reduce((sum, c) => sum + (c.agreedRate || 0), 0);
        // Sum per-stream viewer-hours (ccv × hours), NOT Σccv × Σhours — see note above.
        const sTotalViewerHours = creatorROI.reduce((sum, c) => sum + (c.streamViewerHours || 0), 0);
        const sOverallCPV = sTotalViewerHours > 0 ? sTotalBudgetSpent / sTotalViewerHours : null;
        const sTotalImpressions = creatorROI.reduce((sum, c) => sum + c.totalImpressions, 0);
        // CPM = budget ÷ total PAID reach views (Twitch VOD + YT + IG + TikTok) × 1000
        const sTotalPaidReach = creatorROI.reduce((sum, c) => sum + (c.paidReach || 0), 0);
        const sOverallCPM = sTotalPaidReach > 0 ? (sTotalBudgetSpent / sTotalPaidReach) * 1000 : null;
        const allSponsoredContents = creatorROI.flatMap(c => [...c.streams, ...c.longform, ...c.shortform]);
        const allSponsoredStreams = creatorROI.flatMap(c => c.streams);
        const sTotalViews = creatorROI.reduce((sum, c) => sum + c.totalViews, 0);
        const sTotalEngagement = creatorROI.reduce((sum, c) => sum + c.totalEngagement, 0);
        const sTotalWatchHours = allSponsoredContents.reduce((sum, c) => sum + (c.watchHours || 0), 0);
        const sTotalPeakCCV = allSponsoredStreams.reduce((sum, c) => sum + (c.peakCcv || c.streamPeakCcv || 0), 0);
        const sTotalVodViews = allSponsoredStreams.reduce((sum, c) => sum + (c.vodViews || 0), 0);

        const getBenchmark = (value, low, high, inverse = false) => {
          if (!value) return { label: 'N/A', color: 'text-white' };
          if (inverse) {
            if (value <= low) return { label: 'Great', color: 'text-green-400' };
            if (value <= high) return { label: 'Good', color: 'text-amber-400' };
            return { label: 'High', color: 'text-red-400' };
          }
          return { label: '', color: '' };
        };

        // --- Organic data (organicContents defined above = all non-Paid content) ---
        const oStreams = organicContents.filter(isStream);
        const oTotalViews = organicContents.reduce((sum, c) => sum + reachOf(c), 0);
        // Summed PER ITEM: YouTube content posted on/after 2026-08-24 is
        // estimated at 5x rather than 10x.
        const oTotalImpressions = organicContents.reduce((sum, c) => sum + impressionsFor(c, reachOf(c)), 0);
        const oTotalEngagement = organicContents.reduce((sum, c) => sum + (c.likes || 0) + (c.comments || 0) + (c.shares || 0), 0);
        const oTotalWatchHours = organicContents.reduce((sum, c) => sum + (c.watchHours || 0), 0);
        const oTotalCCV = oStreams.reduce((sum, c) => sum + (c.streamAvgCcv || c.avgCcv || 0), 0);
        const oTotalPeakCCV = oStreams.reduce((sum, c) => sum + (c.peakCcv || c.streamPeakCcv || 0), 0);
        const oTotalVodViews = oStreams.reduce((sum, c) => sum + (c.vodViews || 0), 0);
        // YouTube Views pill = non-stream YouTube views only; YouTube streams'
        // reach is reported under VOD Views (matches the reach model above).
        const oYoutubeViews = organicContents.filter(c => c.platform === 'youtube' && !isStream(c)).reduce((sum, c) => sum + (c.views || 0), 0);

        return (
          <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 mb-6">
            {/* Sponsored */}
            {sponsoredCreators.length > 0 && (
              <div className="bg-[#241d33] border border-white rounded-xl p-4 flex flex-col">
                <h3 className="font-semibold text-white text-sm mb-2">Sponsored Creator ROI</h3>

                {/* Stats pills */}
                <div className="flex flex-wrap gap-1.5 mb-3">
                  <Pill label="Spent" value={`$${formatNum(sTotalBudgetSpent)}`} />
                  <Pill label="Creators" value={sponsoredCreators.length} />
                  <Pill label="Content" value={creatorROI.reduce((s, c) => s + c.contentCount, 0)} />
                  <Pill label="CPV/hr" value={sOverallCPV !== null ? `$${sOverallCPV.toFixed(2)}` : 'N/A'} />
                  <Pill label="CPM" value={sOverallCPM !== null ? `$${sOverallCPM.toFixed(2)}` : 'N/A'} />
                  <Pill label="Views" value={formatNum(sTotalViews)} />
                  <Pill label="Impressions" value={formatNum(sTotalImpressions)} />
                  <Pill label="Engagement" value={formatNum(sTotalEngagement)} />
                  <Pill label="Watch Hours" value={formatNum(Math.round(sTotalWatchHours))} />
                  <Pill label="Peak CCV" value={formatNum(Math.round(sTotalPeakCCV))} />
                  <Pill label="VOD Views" value={formatNum(sTotalVodViews)} />
                </div>

                {/* Per-Creator ROI Table - scrollable */}
                <div className="overflow-x-auto mt-auto">
                  <div className="flex items-center gap-3 mb-2 text-[10px] text-white">
                    <span>CPM = (Budget / Paid views, incl. VOD) x 1,000 — benchmark $50–$55 long-form, $20–$35 short-form</span>
                  </div>
                  <div className="max-h-[180px] overflow-y-auto">
                    <table className="w-full text-xs">
                      <thead className="sticky top-0 bg-[#241d33] z-10">
                        <tr className="border-b border-white/10">
                          <th className="text-left py-1.5 px-1.5 text-[10px] font-medium text-white">Creator</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">Cost</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">Content</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">Streams</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">Videos</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">Stream Duration</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">Combined CCV</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">CPV/hr</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">CPV Rating</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">Views (inc. VOD)</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">CPM</th>
                          <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">CPM Rating</th>
                        </tr>
                      </thead>
                      <tbody>
                        {creatorROI.map(creator => {
                          const cpvBench = getBenchmark(creator.streamCPV, 1.00, 1.25, true);
                          const videoViews = creator.longformViews + creator.shortformViews;

                          return (
                            <tr key={creator.id} className="border-b border-white/5 hover:bg-white/3">
                              <td className="py-1.5 px-1.5">
                                <div className="flex items-center gap-1.5">
                                  {creator.profileImageUrl ? (
                                    <img src={creator.profileImageUrl} alt="" className="w-5 h-5 rounded-full ring-1 ring-white/10" />
                                  ) : (
                                    <div className="w-5 h-5 rounded-full bg-sc-pink/15 flex items-center justify-center text-sc-pink text-[10px] font-semibold">
                                      {creator.name?.charAt(0)?.toUpperCase() || '?'}
                                    </div>
                                  )}
                                  <span className="font-medium text-white truncate max-w-[100px]">{creator.name}</span>
                                </div>
                              </td>
                              <td className="py-1.5 px-1.5 text-right text-white">{creator.sym}{formatNum(creator.cost)}</td>
                              <td className="py-1.5 px-1.5 text-right text-white">{creator.contentCount}</td>
                              <td className="py-1.5 px-1.5 text-right text-white">{creator.streamCount > 0 ? creator.streamCount : 'N/A'}</td>
                              <td className="py-1.5 px-1.5 text-right text-white">{creator.videoCount > 0 ? creator.videoCount : 'N/A'}</td>
                              <td className="py-1.5 px-1.5 text-right text-white">{creator.streamHours > 0 ? `${Math.floor(creator.streamHours)}h ${Math.round((creator.streamHours % 1) * 60)}m` : '-'}</td>
                              <td className="py-1.5 px-1.5 text-right text-white">{creator.streamCount > 0 ? formatNum(creator.streamCombinedCCV) : '-'}</td>
                              <td className="py-1.5 px-1.5 text-right font-medium text-sc-pink">{creator.streamCPV ? formatMoney(creator.streamCPV, creator.sym) : '-'}</td>
                              <td className={`py-1.5 px-1.5 text-right text-[10px] font-semibold ${cpvBench.color}`}>{creator.streamCount > 0 ? cpvBench.label : '-'}</td>
                              <td className="py-1.5 px-1.5 text-right text-white">{(videoViews + creator.streamVodViews) > 0 ? formatNum(videoViews + creator.streamVodViews) : '-'}</td>
                              {(() => {
                                // CPM is a VIDEO metric — only meaningful when the creator actually
                                // posted a video. A stream-only creator's VOD views would otherwise
                                // produce a misleading CPM against the video benchmark, so suppress it.
                                const hasVideo = creator.videoCount > 0;
                                const totalViews = videoViews + creator.streamVodViews;
                                const cpm = (hasVideo && totalViews > 0) ? (creator.cost / totalViews) * 1000 : null;
                                const cpmBench = getBenchmark(cpm, 50, 55, true);
                                return (<>
                              <td className="py-1.5 px-1.5 text-right font-medium text-sc-pink">{cpm ? formatMoney(cpm, creator.sym) : 'N/A'}</td>
                              <td className={`py-1.5 px-1.5 text-right text-[10px] font-semibold ${hasVideo ? cpmBench.color : ''}`}>{hasVideo ? cpmBench.label : '-'}</td>
                              </>); })()}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Organic */}
            {organicContents.length > 0 && (
              <div className="bg-[#241d33] border border-white rounded-xl p-4 flex flex-col">
                <h3 className="font-semibold text-white text-sm mb-2">Organic Creator Performance</h3>

                <div className="flex flex-wrap gap-1.5 mb-3">
                  <Pill label="Creators" value={formatNum(oCreatorCount)} />
                  <Pill label="Content" value={formatNum(organicContents.length)} />
                  <Pill label="Views" value={formatNum(oTotalViews)} />
                  <Pill label="Impressions" value={formatNum(oTotalImpressions)} />
                  <Pill label="Engagement" value={formatNum(oTotalEngagement)} />
                  <Pill label="Watch Hours" value={formatNum(Math.round(oTotalWatchHours))} />
                  <Pill label="CCV" value={formatNum(Math.round(oTotalCCV))} />
                  <Pill label="Peak CCV" value={formatNum(Math.round(oTotalPeakCCV))} />
                  <Pill label="VOD Views" value={formatNum(oTotalVodViews)} />
                  <Pill label="YouTube Views" value={formatNum(oYoutubeViews)} />
                </div>

                {/* Organic content table - scrollable, aligned with sponsored table */}
                {organicContents.length > 0 && (
                  <div className="overflow-x-auto mt-auto">
                    <div className="max-h-[180px] overflow-y-auto">
                      <table className="w-full text-xs">
                        <thead className="sticky top-0 bg-[#241d33] z-10">
                          <tr className="border-b border-white/10">
                            <th className="text-left py-1.5 px-1.5 text-[10px] font-medium text-white">Creator</th>
                            <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">Platform</th>
                            <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">
                              <span className="inline-flex items-center gap-1 justify-end">Views <YouTubeEstimateTooltip metric="watchHours" /></span>
                            </th>
                            <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">CCV</th>
                            <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">
                              <span className="inline-flex items-center gap-1 justify-end">Watch Hours <YouTubeEstimateTooltip metric="watchHours" /></span>
                            </th>
                            <th className="text-right py-1.5 px-1.5 text-[10px] font-medium text-white">Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {[...organicContents]
                            .sort((a, b) => new Date(b.createdAt || b.publishedAt || 0) - new Date(a.createdAt || a.publishedAt || 0))
                            .map(content => {
                              const creator = creators.find(cr => cr.id === (content.creatorId || content.creator?.id)) || content.creator;
                              const contentDate = content.publishedAt || content.createdAt || content.streamDate;
                              const dateStr = contentDate ? new Date(contentDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '-';

                              return (
                                <tr key={content.id || content.contentUrl} className="border-b border-white/5 hover:bg-white/3">
                                  <td className="py-1.5 px-1.5">
                                    <div className="flex items-center gap-1.5">
                                      {creator?.profileImageUrl ? (
                                        <img src={creator.profileImageUrl} alt="" className="w-5 h-5 rounded-full ring-1 ring-white/10" />
                                      ) : (
                                        <div className="w-5 h-5 rounded-full bg-sc-pink/15 flex items-center justify-center text-sc-pink text-[10px] font-semibold">
                                          {creator?.name?.charAt(0)?.toUpperCase() || '?'}
                                        </div>
                                      )}
                                      <span className="font-medium text-white truncate max-w-[100px]">{creator?.name || 'Unknown'}</span>
                                    </div>
                                  </td>
                                  <td className="py-1.5 px-1.5 text-right text-white">{platformDisplay(content.platform)}</td>
                                  <td className="py-1.5 px-1.5 text-right text-white">{(content.views || content.vodViews) ? formatNum(content.views || content.vodViews) : '-'}</td>
                                  <td className="py-1.5 px-1.5 text-right text-white">{(content.streamAvgCcv || content.avgCcv) ? formatNum(content.streamAvgCcv || content.avgCcv) : '-'}</td>
                                  <td className="py-1.5 px-1.5 text-right text-white">{content.watchHours ? formatNum(Math.round(content.watchHours)) : '-'}</td>
                                  <td className="py-1.5 px-1.5 text-right text-white whitespace-nowrap">{dateStr}</td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      {/* Content View */}
      <h3 className="font-semibold text-white text-sm mb-3">All Coverage</h3>
      <CampaignContentView
        campaign={campaign}
        contents={contents}
        creators={creators}
        onContentClick={(content) => {
          if (content.contentUrl) window.open(content.contentUrl, '_blank');
        }}
        onRefresh={() => fetchCampaign(true, false)}
      />
      </>)}

      {/* Modals */}
      <ContentScannerModal
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        campaignId={id}
        campaignName={campaign?.name}
        onContentAdded={() => fetchCampaign()}
      />
      <CampaignAutomationSettings
        campaignId={id}
        isOpen={showAutomation}
        onClose={() => setShowAutomation(false)}
      />
      <BulkCreatorUpload
        isOpen={showBulkUpload}
        onClose={() => setShowBulkUpload(false)}
        campaignId={id}
        onSuccess={() => fetchCampaign()}
        apiEndpoint={(creators) => campaignsAPI.bulkAddCreators(id, creators)}
      />
      <AddContentModal
        isOpen={showAddContent}
        onClose={() => setShowAddContent(false)}
        campaignId={id}
        onSuccess={() => fetchCampaign()}
      />
      <EditCampaignModal
        isOpen={showEditCampaign}
        onClose={() => setShowEditCampaign(false)}
        campaign={campaign}
        onSuccess={() => fetchCampaign()}
      />
      <KeyUploadModal
        isOpen={showKeyUpload}
        onClose={() => setShowKeyUpload(false)}
        campaignId={id}
        onSuccess={() => fetchCampaign()}
      />
      <ExportOptionsModal
        isOpen={showExportOptions}
        onClose={() => setShowExportOptions(false)}
        onExport={handleExportCSV}
        campaignName={campaign?.name}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-[#1a1525] border border-white rounded-xl p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-white mb-2">Delete Campaign</h3>
            <p className="text-white mb-4">
              Are you sure you want to delete <strong className="text-white">{campaign?.name}</strong>?
            </p>
            <p className="text-sm text-white mb-6">
              The campaign will be permanently deleted, but all content will be preserved on creator profiles.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                disabled={deleting}
                className="px-4 py-2 bg-white/5 border border-white text-white rounded-lg hover:bg-white/10 disabled:opacity-50 text-sm"
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteCampaign}
                disabled={deleting}
                className="px-4 py-2 bg-red-500/20 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/30 disabled:opacity-50 flex items-center gap-2 text-sm font-medium"
              >
                {deleting ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> Deleting...</>
                ) : (
                  'Delete Campaign'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Creators Slide-out Panel */}
      {showCreatorsWidget && (
        <CampaignCreatorsList
          creators={creators}
          campaignId={id}
          compact={true}
          initialOpen={true}
          onRemoveCreator={(creatorId) => {
            setCreators(prev => prev.filter(c => c.id !== creatorId));
            fetchCampaign();
          }}
          onCreatorUpdated={() => fetchCampaign(true, true)}
          onPanelClose={() => setShowCreatorsWidget(false)}
        />
      )}

      {/* Floating Creators Button */}
      {!showCreatorsWidget && (
        <div className="fixed bottom-6 left-6 z-40">
          <button
            onClick={() => setShowCreatorsWidget(true)}
            className="flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg transition-all bg-sc-pink hover:bg-sc-pink/90 text-white text-sm font-medium"
          >
            <Users size={14} />
            Creators ({creators.length})
          </button>
        </div>
      )}

      {/* Floating Live Stream Widget */}
      <div className="fixed bottom-6 right-6 z-40">
        {showLiveWidget && (
          <div className="mb-3 w-80 bg-[#241d33] border border-white rounded-xl shadow-2xl shadow-black/50 overflow-hidden">
            <LiveStreamMonitor
              campaignId={id}
              campaignName={campaign?.name}
              gameName={campaign?.game?.title}
              compact={true}
            />
          </div>
        )}
        <button
          onClick={() => setShowLiveWidget(prev => !prev)}
          className={`ml-auto flex items-center gap-2 px-4 py-2.5 rounded-full shadow-lg transition-all ${
            showLiveWidget
              ? 'bg-pink-600 hover:bg-pink-700'
              : 'bg-sc-pink hover:bg-sc-pink/90'
          } text-white text-sm font-medium`}
        >
          <span className="relative flex h-2.5 w-2.5">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
          </span>
          {showLiveWidget ? 'Close' : 'Live Now'}
        </button>
      </div>
    </div>
  );
};

export default CampaignDetail;
